/**
 * Per-session cache for a tenant's OSM-sourced road list, mirroring
 * `streetAliasCache.ts`'s shape. Read by `MapSearchControl` so the list is
 * fetched once per tenant rather than once per search-icon click.
 */
// Legacy JS API module — typed via the permissive `@/api/*` shim, so the
// response needs a cast.
import * as osmRoadsApi from '@/api/osmRoads';

export interface OsmRoadRecord {
  id: number;
  tenant_id: number;
  name: string;
  /** One entry per OSM way (a road split at intersections has several),
   *  each a [lat, lon] node chain. */
  segments: Array<Array<[number, number]>>;
  /** Representative point — the midpoint of the longest segment. */
  point: [number, number] | null;
}

interface OsmRoadListResponse {
  roads?: OsmRoadRecord[];
}

const cache = new Map<number, OsmRoadRecord[]>();
const inFlight = new Map<number, Promise<OsmRoadRecord[]>>();

/**
 * Fetch (or return the cached) OSM road list for a tenant.
 *
 * A failed fetch resolves to `[]` and is NOT cached, so a later caller can
 * retry — like street aliases, this is a search enhancement, not a
 * blocker: a failure just means "Other roads" results don't show up.
 */
export function loadTenantOsmRoads(tenantId: number): Promise<OsmRoadRecord[]> {
  const cached = cache.get(tenantId);
  if (cached) return Promise.resolve(cached);

  const pending = inFlight.get(tenantId);
  if (pending) return pending;

  const promise = (osmRoadsApi.listOsmRoads(tenantId) as Promise<OsmRoadListResponse>)
    .then((data) => {
      const list = data?.roads ?? [];
      cache.set(tenantId, list);
      return list;
    })
    .catch((err) => {
      console.warn('Failed to load OSM roads:', err);
      return [] as OsmRoadRecord[];
    })
    .finally(() => {
      inFlight.delete(tenantId);
    });

  inFlight.set(tenantId, promise);
  return promise;
}

/** Drop the cached list for a tenant — call after an admin-triggered refresh. */
export function invalidateTenantOsmRoads(tenantId: number): void {
  cache.delete(tenantId);
}
